import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { makeStyles, Box, Typography, Button, Link, TextField, Grid } from '@material-ui/core';
import { ArrowBackIcon } from 'components/icons';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  backIcon: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  intro: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  button: {
    width: '100%',
    fontWeight: 300,
    fontSize: '20px',
    lineHeight: '24px',
    textTransform: 'unset',
  },

  formGroupGrid: {
    width: '100%',
    flexGrow: 1,
  },
}));

const FieldSideBarDraw = ({ field, errors, onSave, onCancel, setValues, onPrev }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [soil_texture, setSoilTexture] = useState({ sand: Number(''), silt: Number(''), clay: Number('') });
  const [soil_density, setSoilDensity] = useState();
  const [showTextureError, setShowTextureError] = useState(false);

  useEffect(() => {
    if (field?.soil_density) {
      setSoilDensity(field.soil_density);
    } else {
      setSoilDensity();
    }

    if (field?.soil_texture) {
      setSoilTexture(field.soil_texture);
    } else {
      setSoilTexture({ sand: Number(''), silt: Number(''), clay: Number('') });
    }
  }, []);

  const handleFieldChange = (event, name, value) => {
    if (name == 'sand' || name == 'silt' || name == 'clay') {
      if (Number(value) <= 100 && texturesAreValid(name, value)) {
        let newTexture = soil_texture;
        newTexture[name] = Number(value);
        setSoilTexture(newTexture);
        setValues({ ...field, soil_texture: newTexture });
      }
    } else if (name == 'soil_density') {
      let newDensity = value;
      setSoilDensity(newDensity);
      setValues({ ...field, [name]: Number(newDensity) });
    } else if (event.persist) {
      setValues({ ...field, [name]: value });
    }
  };

  const handleSave = () => {
    setValues({ ...field, soil_density: Number(soil_density) });
    setValues({ ...field, soil_texture: soil_texture });

    onSave();
  };

  const texturesAreValid = (name, value) => {
    let newTexture = soil_texture;
    newTexture[name] = Number(value);
    if (Number(newTexture['clay']) + Number(newTexture['sand']) + Number(newTexture['silt']) <= 100) {
      setShowTextureError(false);
      return true;
    } else {
      setShowTextureError(true);
      return false;
    }
  };

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box>
        <Link onClick={onPrev} className={classes.backIcon}>
          <ArrowBackIcon size="small" />
          <Typography style={{ paddingLeft: 8 }}>{t('how_add_fields')}</Typography>
        </Link>
      </Box>
      <Box pb={2}>
        <Typography variant="h4" className={classes.intro}>
          {t('draw_fields_title')}
        </Typography>
      </Box>
      <Box pb={2}>
        <div className={classes.formGroupGrid}>
          <TextField
            fullWidth
            label={t('field_name')}
            name="name"
            size="small"
            autoComplete="name"
            onChange={(event) => handleFieldChange(event, 'name', event.target.value)}
            value={field?.name}
            placeholder={t('field_name')}
            helperText={errors && errors.name}
            error={!!(errors && errors.name)}
            inputProps={{
              maxLength: 24,
            }}
            required
          />
        </div>

        <Grid item container xs={12}>
          <div>
            <TextField
              fullWidth
              label={t('soil_density')}
              name="soil_density"
              size="small"
              autoComplete="soil_density"
              onChange={(event) => handleFieldChange(event, 'soil_density', event.target.value)}
              value={soil_density}
              placeholder={t('soil_density')}
              inputProps={{
                maxLength: 24,
              }}
              required
            />
          </div>
        </Grid>

        <Grid item container xs={12}>
          <div>
            <Typography variant="subtitle2" gutterBottom component="div">
              {t('soil_texture')}
            </Typography>
          </div>
          {showTextureError && (
            <Grid item container xs={12}>
              <Typography style={{ color: 'red' }} variant="subtitle2" gutterBottom component="div">
                Clay, sand, and silt must total 100 or less.
              </Typography>
            </Grid>
          )}

          <Grid item container xs={12}>
            <TextField
              fullWidth
              label={t('sand')}
              name="sand"
              size="small"
              autoComplete="sand"
              onChange={(event) => handleFieldChange(event, 'sand', event.target.value)}
              value={soil_texture['sand']}
              placeholder={t('sand')}
              inputProps={{
                maxLength: 24,
              }}
              required
            />
          </Grid>
          <Grid item container xs={12}>
            <TextField
              fullWidth
              label={t('silt')}
              name="silt"
              size="small"
              autoComplete="silt"
              onChange={(event) => handleFieldChange(event, 'silt', event.target.value)}
              value={soil_texture['silt']}
              placeholder={t('silt')}
              inputProps={{
                maxLength: 24,
              }}
              required
            />
          </Grid>
          <Grid item container xs={12}>
            <TextField
              fullWidth
              label={t('clay')}
              name="clay"
              size="small"
              autoComplete="clay"
              onChange={(event) => handleFieldChange(event, 'clay', event.target.value)}
              value={soil_texture['clay']}
              placeholder={t('clay')}
              inputProps={{
                maxLength: 24,
              }}
              required
            />
          </Grid>
        </Grid>
      </Box>
      <Box pb={2}>
        <Typography component="div">{t('place_points')}</Typography>
      </Box>
      <Box flexGrow={1}>
        <Button variant="contained" disableElevation key="cancel" onClick={onCancel}>
          {t('cancel')}
        </Button>
      </Box>
      <Box>
        <Button
          disabled={!field?.name || !field?.geometry || showTextureError}
          color="primary"
          variant="contained"
          className={classes.button}
          onClick={handleSave}
        >
          {t('save')}
        </Button>
      </Box>
    </Box>
  );
};

FieldSideBarDraw.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  field: PropTypes.object.isRequired,
  errors: PropTypes.object,
  createPolygon: PropTypes.func,
  setValues: PropTypes.func.isRequired,
};

FieldSideBarDraw.defaultProps = {
  errors: {},
};

export default FieldSideBarDraw;
